import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box, Typography, Button, TextField, CircularProgress,
  Autocomplete, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  Collapse, Skeleton,
} from '@mui/material';
import { MessageSquare, Mail, Send, Check, ChevronsRight, ChevronDown, ChevronRight } from 'react-feather';
import axios from '../../utils/axios';
import { config } from '../../constants';

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const now = new Date();
  const d = new Date(dateStr);
  const diff = (now - d) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString();
};

const SmsSidebar = ({ onClose }) => {
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [channel, setChannel] = useState('sms');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sendState, setSendState] = useState('idle');
  const [conversations, setConversations] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [expandedThreads, setExpandedThreads] = useState({});
  const [threadMessages, setThreadMessages] = useState({});
  const [loadingThreads, setLoadingThreads] = useState({});
  const [phoneDialog, setPhoneDialog] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [contactForPhone, setContactForPhone] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [debouncedInput, setDebouncedInput] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedInput(inputValue), 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const filteredContacts = useMemo(() => {
    if (!debouncedInput) return contacts;
    const lower = debouncedInput.toLowerCase();
    return contacts.filter(opt =>
      (opt.name || '').toLowerCase().includes(lower) ||
      (opt.customer_name || '').toLowerCase().includes(lower) ||
      (opt.phone || '').includes(lower)
    );
  }, [debouncedInput, contacts]);

  const filteredConversations = useMemo(() => {
    if (!debouncedInput) return conversations;
    const lower = debouncedInput.toLowerCase();
    return conversations.filter(conv =>
      (conv.person_name || '').toLowerCase().includes(lower) ||
      (conv.company_name || '').toLowerCase().includes(lower)
    );
  }, [debouncedInput, conversations]);

  useEffect(() => {
    fetchContacts();
    fetchConversations();
  }, []);

  const fetchContacts = async () => {
    try {
      setLoadingContacts(true);
      const response = await axios.get(`${config.url.SMS_URL}/api/sms/contacts`);
      setContacts(response.data.contacts || []);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    } finally {
      setLoadingContacts(false);
    }
  };

  const fetchConversations = async () => {
    try {
      setLoadingConversations(true);
      const response = await axios.get(`${config.url.SMS_URL}/api/sms/inbox/conversations`);
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoadingConversations(false);
    }
  };

  const fetchThreadMessages = async (threadId) => {
    if (threadMessages[threadId]) {
      return;
    }
    try {
      setLoadingThreads(prev => ({ ...prev, [threadId]: true }));
      const response = await axios.get(`${config.url.SMS_URL}/api/sms/conversations/thread/${threadId}?skip_sync=true`);
      setThreadMessages(prev => ({ ...prev, [threadId]: response.data.messages || [] }));
    } catch (error) {
      console.error('Failed to fetch thread messages:', error);
    } finally {
      setLoadingThreads(prev => ({ ...prev, [threadId]: false }));
    }
  };

  const handleContactSelect = (event, contact) => {
    if (!contact) {
      setSelectedContact(null);
      setInputValue('');
      return;
    }

    if (!contact.phone && channel === 'sms') {
      setContactForPhone(contact);
      setPhoneDialog(true);
      return;
    }

    setSelectedContact(contact);
    setInputValue(contact.name || contact.customer_name || '');
  };

  const handlePhoneDialogSubmit = async () => {
    if (!phoneInput || !contactForPhone) return;

    try {
      await axios.put(`${config.url.SMS_URL}/api/sms/contacts/${contactForPhone.id}`, {
        phone: phoneInput
      });

      const updatedContact = { ...contactForPhone, phone: phoneInput };
      setContacts(prev => prev.map(c => c.id === contactForPhone.id ? updatedContact : c));
      setSelectedContact(updatedContact);
      setPhoneDialog(false);
      setPhoneInput('');
      setContactForPhone(null);
    } catch (error) {
      console.error('Failed to update contact phone:', error);
    }
  };

  const handleSend = async () => {
    if (!selectedContact || !body.trim()) return;

    try {
      setSendState('sending');
      await axios.post(`${config.url.SMS_URL}/api/sms/compose`, {
        contact_id: selectedContact.id,
        channel,
        body,
        subject: channel === 'email' ? subject : undefined
      });

      setSendState('sent');
      setBody('');
      setSubject('');
      fetchConversations();

      setTimeout(() => {
        setSendState('idle');
      }, 2000);
    } catch (error) {
      console.error('Failed to send message:', error);
      setSendState('idle');
    }
  };

  const handleConversationClick = async (conv) => {
    const isExpanded = expandedThreads[conv.nylas_thread_id];

    if (!isExpanded) {
      setSelectedContact({
        id: conv.contact_id,
        name: conv.person_name,
        customer_name: conv.company_name,
        phone: conv.phone,
        email: conv.email
      });
      setChannel(conv.channel);

      await fetchThreadMessages(conv.nylas_thread_id);
    }

    setExpandedThreads(prev => ({
      ...prev,
      [conv.nylas_thread_id]: !isExpanded
    }));
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'white' }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid #e0e0e0',
        px: 2,
        py: 1.5
      }}>
        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
          Messages
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Button
            variant={channel === 'sms' ? 'contained' : 'outlined'}
            onClick={() => setChannel('sms')}
            startIcon={<MessageSquare size={14} />}
            sx={{
              textTransform: 'none',
              fontSize: '0.8rem',
              height: 34,
              borderRadius: 0.5,
              px: 2,
              minWidth: 'auto',
              ...(channel === 'sms'
                ? { bgcolor: '#1d4ed8', '&:hover': { bgcolor: '#1e40af' } }
                : { color: '#64748b', borderColor: '#e0e0e0' }),
            }}
          >
            SMS
          </Button>
          <Button
            variant={channel === 'email' ? 'contained' : 'outlined'}
            onClick={() => setChannel('email')}
            startIcon={<Mail size={14} />}
            sx={{
              textTransform: 'none',
              fontSize: '0.8rem',
              height: 34,
              borderRadius: 0.5,
              px: 2,
              minWidth: 'auto',
              ...(channel === 'email'
                ? { bgcolor: '#1d4ed8', '&:hover': { bgcolor: '#1e40af' } }
                : { color: '#64748b', borderColor: '#e0e0e0' }),
            }}
          >
            Email
          </Button>
          <Button
            size="small"
            onClick={onClose}
            sx={{ minWidth: 'auto', p: 0.5, ml: 0.5 }}
          >
            <ChevronsRight size={20} />
          </Button>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Section 1: Recipient + Channel */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Autocomplete
            size="small"
            forcePopupIcon={false}
            options={filteredContacts}
            filterOptions={(x) => x}
            loading={loadingContacts}
            value={selectedContact}
            inputValue={inputValue}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === 'reset') return;
              setInputValue(newInputValue);
            }}
            componentsProps={{ popper: { style: { zIndex: 10000 } } }}
            onChange={handleContactSelect}
            getOptionLabel={(option) => option.name || option.customer_name || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box sx={{ width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        color: !option.phone ? '#d32f2f' : 'inherit'
                      }}
                    >
                      {option.name || option.customer_name || 'Unknown'}
                    </Typography>
                    {option.name && option.customer_name && (
                      <Chip
                        label={option.customer_name}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.65rem',
                          bgcolor: '#f5f5f5',
                          color: '#666'
                        }}
                      />
                    )}
                  </Box>
                  <Typography sx={{ fontSize: '0.75rem', color: '#666' }}>
                    {option.phone || option.email || 'No contact info'}
                  </Typography>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search contacts..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingContacts && <CircularProgress size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />

          {channel === 'email' && (
            <TextField
              size="small"
              fullWidth
              placeholder="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          )}
        </Box>

        {/* Section 2: Compose + Send */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <TextField
            multiline
            rows={5}
            size="small"
            fullWidth
            placeholder="Type your message..."
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />

          <Button
            variant="contained"
            fullWidth
            disabled={!selectedContact || !body.trim() || sendState === 'sending'}
            onClick={handleSend}
            startIcon={
              sendState === 'sending' ? (
                <CircularProgress size={16} color="inherit" />
              ) : sendState === 'sent' ? (
                <Check size={16} />
              ) : (
                <Send size={16} />
              )
            }
            sx={{
              py: 1.5,
              fontSize: '0.9rem',
              bgcolor: sendState === 'sent' ? '#2e7d32' : undefined,
              '&:hover': {
                bgcolor: sendState === 'sent' ? '#2e7d32' : undefined
              }
            }}
          >
            {sendState === 'sending' ? 'Sending...' : sendState === 'sent' ? 'Sent!' : 'Send'}
          </Button>
        </Box>

        {/* Section 3: History */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <Typography variant="subtitle2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#666', mb: 0.5 }}>
            Recent Conversations
          </Typography>

          {loadingConversations ? (
            <>
              <Skeleton variant="rectangular" height={30} sx={{ mb: 0.5 }} />
              <Skeleton variant="rectangular" height={30} sx={{ mb: 0.5 }} />
              <Skeleton variant="rectangular" height={30} sx={{ mb: 0.5 }} />
            </>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {filteredConversations.map((conv) => (
                <Box key={conv.nylas_thread_id}>
                  <Box
                    onClick={() => handleConversationClick(conv)}
                    sx={{
                      py: 0.5,
                      px: 1,
                      borderBottom: '1px solid #f0f0f0',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: '#f5f5f5' },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75
                    }}
                  >
                    {conv.channel === 'sms' ? (
                      <MessageSquare size={12} color="#2e7d32" style={{ flexShrink: 0 }} />
                    ) : (
                      <Mail size={12} color="#1976d2" style={{ flexShrink: 0 }} />
                    )}
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {conv.person_name || conv.subject}
                      {conv.company_name && <span style={{ fontWeight: 400, color: '#999', marginLeft: 4 }}>{conv.company_name}</span>}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                      <Typography sx={{ fontSize: '0.65rem', color: '#999' }}>
                        {timeAgo(conv.last_activity)}
                      </Typography>
                      {expandedThreads[conv.nylas_thread_id] ? (
                        <ChevronDown size={12} />
                      ) : (
                        <ChevronRight size={12} />
                      )}
                    </Box>
                  </Box>

                  <Collapse in={expandedThreads[conv.nylas_thread_id]}>
                    <Box sx={{ pl: 2, pr: 2, pt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {loadingThreads[conv.nylas_thread_id] ? (
                        <CircularProgress size={20} />
                      ) : (
                        threadMessages[conv.nylas_thread_id]?.map((msg, idx) => {
                          const isSent = msg.status === 'sent';
                          return (
                            <Box
                              key={idx}
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignSelf: isSent ? 'flex-end' : 'flex-start',
                                maxWidth: '80%'
                              }}
                            >
                              <Box
                                sx={{
                                  bgcolor: isSent ? '#1976d2' : '#f5f5f5',
                                  color: isSent ? 'white' : 'text.primary',
                                  borderRadius: isSent ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                                  px: 1.5,
                                  py: 1
                                }}
                              >
                                <Typography sx={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                                  {msg.message}
                                </Typography>
                              </Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '0.65rem',
                                  color: '#999',
                                  textAlign: isSent ? 'right' : 'left',
                                  mt: 0.25
                                }}
                              >
                                {timeAgo(msg.sent_at)}
                              </Typography>
                            </Box>
                          );
                        })
                      )}
                    </Box>
                  </Collapse>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* Phone Dialog */}
      <Dialog open={phoneDialog} onClose={() => setPhoneDialog(false)}>
        <DialogTitle>Add Phone Number</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This contact needs a phone number to send SMS.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Phone number"
            value={phoneInput}
            onChange={(e) => setPhoneInput(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPhoneDialog(false)}>Cancel</Button>
          <Button onClick={handlePhoneDialogSubmit} variant="contained" disabled={!phoneInput}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SmsSidebar;
